import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import 'react-phone-number-input/style.css';
import API from '../../utils/API';
import * as modalUtils from '../../utils/modalUtils';
import useModalInitialization from '../../hooks/useModalInitialization';
import constants from '../../utils/constants'

const modalId = 'usersAssignSiteModal';

function UsersAssignSiteModal(props) {
  const { selectedUserIds, setSuccess, setError, dataMap, loadTable, usersData } = props;
  
  // Use the modal initialization hook
  const {
    // State
    cms,
    cmses,
    selectedSiteIds,
    cmsDropdownError,
    siteDropdownError,
    
    // Setters
    setCmsDropdownError,
    
    // Computed values
    selectedSitesCount,
    selectedSitesText,
    
    // Handlers
    handleCmsClick,
    closeModal,
    
    // Helper functions
    generateSiteComponents
  } = useModalInitialization({
    modalId,
    selectedUserIds,
    dataMap,
    siteExtractor: modalUtils.extractStringBasedSiteInfo
  });
  
  // Feature-specific state (not handled by the hook)
  const [features] = useState([{id: constants.featureIds.msgManager/*"f07b57b8-a1a0-470a-8dc8-6811e649eab2"*/, name: 'Message Manager'}, {id: constants.featureIds.signagePlus/*"9aeaf825-0152-49b0-aa38-79ae9998ef6e"*/, name: 'Signage Plus'}]);
  const [selectedFeatureIds, setFeatureIds] = useState({/*"f07b57b8-a1a0-470a-8dc8-6811e649eab2":true*/});
  const [featureDropdownError, setFeatureDropdownError] = useState('');
  const [featureSiteWarning, setFeatureSiteWarning] = useState('');

  /**
   * Extract feature-specific site information from raw user data
   * @param {Object} user - Raw user object from API with sbmm_sites, sbds_sites arrays
   * @returns {Object} Object mapping feature IDs to arrays of site names
   */
  const extractUserFeatureSites = useCallback((user) => {
    const featureSites = {};
    
    // Message Manager sites
    if (user.sbmm_sites && Array.isArray(user.sbmm_sites) && user.sbmm_sites.length > 0) {
      featureSites[constants.featureIds.msgManager] = user.sbmm_sites
        .filter(site => site && site.name)
        .map(site => site.name)
        .sort();
    }
    
    // Signage Plus sites
    if (user.sbds_sites && Array.isArray(user.sbds_sites) && user.sbds_sites.length > 0) {
      featureSites[constants.featureIds.signagePlus] = user.sbds_sites
        .filter(site => site && site.name)
        .map(site => site.name)
        .sort();
    }
    
    return featureSites;
  }, []);

  /**
   * Determine which features should be prefilled for multiple users using intersection logic
   * @param {Array} users - Array of raw user objects from API
   * @returns {Object} Object with features to prefill and optional warning message
   */
  const determinePrefilledFeaturesForMultipleUsers = useCallback((users) => {
    if (!users || users.length === 0) {
      return { features: null, warning: null };
    }
    
    // Step 1: Extract feature-site mappings for all users
    const allUserFeatureSites = users.map(user => extractUserFeatureSites(user));
    
    // Step 2: Find intersection of features (features that ALL users have)
    const firstUserFeatures = Object.keys(allUserFeatureSites[0]);
    const commonFeatures = firstUserFeatures.filter(featureId => 
      allUserFeatureSites.every(userFeatureSites => featureId in userFeatureSites)
    );
    
    // No common features across all users
    if (commonFeatures.length === 0) {
      return { features: null, warning: null };
    }
    
    // Step 3: For each common feature, check if all users have the same sites
    const featureSiteConsistency = {};
    let hasInconsistentSites = false;
    let hasDifferentSitesPerFeature = false;
    
    commonFeatures.forEach(featureId => {
      // Get all site arrays for this feature across all users
      const siteArraysForFeature = allUserFeatureSites.map(ufs => ufs[featureId]);
      
      // Check if all users have the same sites for this feature
      const allUsersHaveSameSitesForFeature = siteArraysForFeature.every((sites, index) => {
        if (index === 0) return true;
        const firstSites = siteArraysForFeature[0];
        return sites.length === firstSites.length && 
               sites.every((site, i) => site === firstSites[i]);
      });
      
      featureSiteConsistency[featureId] = allUsersHaveSameSitesForFeature;
      
      if (!allUsersHaveSameSitesForFeature) {
        hasInconsistentSites = true;
      }
    });
    
    // Step 4: Check if different features have different sites (within a single user)
    // This checks if the common features have different sites from each other
    if (commonFeatures.length > 1 && !hasInconsistentSites) {
      // Take the first user's site arrays for the common features
      const firstUserSiteArrays = commonFeatures.map(fId => allUserFeatureSites[0][fId]);
      const allFeaturesHaveSameSites = firstUserSiteArrays.every((sites, index) => {
        if (index === 0) return true;
        const firstFeatureSites = firstUserSiteArrays[0];
        return sites.length === firstFeatureSites.length && 
               sites.every((site, i) => site === firstFeatureSites[i]);
      });
      
      if (!allFeaturesHaveSameSites) {
        hasDifferentSitesPerFeature = true;
      }
    }
    
    // Step 5: Determine warning message
    let warning = null;
    if (hasInconsistentSites && users.length > 1) {
      warning = 'USERS_HAVE_DIFFERENT_SITES';
    } else if (hasDifferentSitesPerFeature) {
      warning = 'USER_HAS_DIFFERENT_SITES_PER_FEATURE';
    }
    
    // Step 6: Prefill all common features
    const features = {};
    commonFeatures.forEach(fId => features[fId] = true);
    
    return { features, warning };
  }, [extractUserFeatureSites]);

  // Reset feature error and prefill features when modal is shown/hidden
  useEffect(() => {
    const modal = document.getElementById(modalId);
    
    if (modal) {
      const handleModalShow = () => {
        setFeatureDropdownError('');
        setFeatureSiteWarning('');
        
        // Prefill features if appropriate (works for single or multiple users)
        if (selectedUserIds && selectedUserIds.length > 0 && usersData) {
          const selectedUsers = selectedUserIds
            .map(id => usersData.find(u => u.id === id))
            .filter(u => u); // Remove any undefined users
          
          if (selectedUsers.length > 0) {
            console.log('selectedUsers:', selectedUsers);
            const prefilledData = determinePrefilledFeaturesForMultipleUsers(selectedUsers);
            
            if (prefilledData.features && Object.keys(prefilledData.features).length > 0) {
              setFeatureIds(prefilledData.features);
              if (prefilledData.warning) {
                setFeatureSiteWarning(prefilledData.warning);
              }
            }
          }
        }
      };
      
      const handleModalHidden = () => {
        setFeatureDropdownError('');
        setFeatureSiteWarning('');
        // Reset features when modal is closed
        setFeatureIds({});
      };
      
      modal.addEventListener('show.bs.modal', handleModalShow);
      modal.addEventListener('hidden.bs.modal', handleModalHidden);
      
      return () => {
        modal.removeEventListener('show.bs.modal', handleModalShow);
        modal.removeEventListener('hidden.bs.modal', handleModalHidden);
      };
    }
  }, [selectedUserIds, usersData, determinePrefilledFeaturesForMultipleUsers]);

  const onSave = async () => {
    if (!cms) {
      setCmsDropdownError(<FormattedMessage id="NO_CMS_SELECTED" />);
    }
    //Bug #19054 Cannot De-assign Roles or Sites from users.
    /*if (selectedSitesCount === 0) {
      setSiteDropdownError(<FormattedMessage id="NO_SITES_SLECTED" />);
    }*/
    if (selectedFeaturesCount === 0) {
      setFeatureDropdownError(<FormattedMessage id="NO_FEATURES_SELECTED" />);
    }
    if (!cms /*|| selectedSitesCount === 0*/ || selectedFeaturesCount === 0) {
      return; // Prevent API call if there's an error
    }

    try {
      const featureIdsToAssign = Object.keys(selectedFeatureIds).filter((featureId) => selectedFeatureIds[featureId] === true);
      await Promise.all(
        featureIdsToAssign.map((featureId) => {
          switch (featureId) {
            case constants.featureIds.msgManager: // Message Manager
              return API.putAssignSitesForMsgManagerUsr(cms, selectedSiteIds, selectedUserIds);
            case constants.featureIds.signagePlus: // Signage Plus
              return API.putAssignSitesForSignagePlusUsr(cms, selectedSiteIds, selectedUserIds);
            default:
              console.warn(`Unknown feature ID: ${featureId}`);
              return Promise.resolve();
          }
        })
      );

      setSuccess(<FormattedMessage id="ASSIGN_SITE_SUCCESS" />);
      closeModal();
      await loadTable(dataMap, false);
    } catch (e) {
      console.error(e);
      setError(<FormattedMessage id="ASSIGN_SITE_FAIL" />);
    }
  };



  // Get site components from the hook
  const siteComponents = generateSiteComponents();
  const hasSites = siteComponents.length > 0;

  function handleFeatureClick(featureId, e) {
    e.stopPropagation();
    let selectedFeaCounts = Object.keys(selectedFeatureIds).length;
    if (selectedFeaCounts > 0 && featureId in selectedFeatureIds) {
      selectedFeatureIds[featureId] = !selectedFeatureIds[featureId];
    } else {
      selectedFeatureIds[featureId] = true;
    }
    const updatedFeatureIds = { ...selectedFeatureIds };
    setFeatureIds(updatedFeatureIds);
  }
  const generateFeatureComponents = (tier = 0) => {
       let featureComponents = Object.keys(features).map((key) => {
        const padding = tier * 8;
        return (
          <li style={{ paddingLeft: padding }} key={features[key].id} value={features[key].id} onClick={(e) => { handleFeatureClick(features[key].id, e) }}>
            <span className='dropdown-item'>
              <input 
                key={`checkbox-${features[key].id}`} 
                type="checkbox" style={{ marginRight: 8 }} 
                /*disabled={false}*/ 
                checked={selectedFeatureIds[features[key].id] === true}
                readOnly
              />
              {/*features[key].name*/}
              <FormattedMessage id={`${features[key].id}-FEATURENAME`} />
            </span>
          </li>
        );
      })
      return featureComponents;
  };
  const featureComponents = generateFeatureComponents();

  const selectedFeatureNames =  Object.keys(selectedFeatureIds).filter(id => selectedFeatureIds[id]).map(id => {
    let feature = features.find(f => f.id === id);
    return feature ? feature.name : '';
  }).filter(name => name !== '');

  const selectedFeaturesCount = selectedFeatureNames.length;
  const selectedFeaturesText = selectedFeatureNames.join(', ');

  return (
    <div className="modal fade" id={modalId} tabIndex="-1" aria-labelledby="roleModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ overflow: 'visible' }}>
          <div className="modal-header">
            <h5 className="modal-title"><FormattedMessage id="ASSIGN_CMS_SITE" defaultMessage="Assign CMS/Site" /></h5>
            <button type="button" id="closeSitesModal" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={closeModal}></button>
          </div>
          <div className="modal-body" style={{ overflow: 'visible' }}>
          <div style={{ fontWeight: 'bolder' }}><FormattedMessage id="FEATURE" /></div>
            <div className="dropdown">
              <button
                style={{ width: '100%', textAlign: 'left', backgroundColor: 'white', color: 'black', border: '1px solid #ced4da' }}
                className="btn btn-secondary form-select"
                type="button"
                id="featureDropdownMenu"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={/*true*/false}
              >
                 <span style={{ fontWeight: 'bold' }}>{selectedFeaturesCount} <FormattedMessage id="SELECTED_TEXT" /></span>
                 <span style={{ color: '#6c757d', marginLeft: '10px' }}>{selectedFeaturesText}</span>
              </button>
              <ul
                style={{ textAlign: 'left'}}
                className="dropdown-menu"
                aria-labelledby="featureDropdownMenu"
              >
                {featureComponents}
  
              </ul>
            </div>
            {featureDropdownError && <div className="text-danger" role="alert">{featureDropdownError}</div>}
            {featureSiteWarning && (
              <div className="alert alert-warning mt-2" role="alert" style={{ fontSize: '0.9em' }}>
                <FormattedMessage id={featureSiteWarning} defaultMessage="This user has different sites assigned to different features. Saving will update all selected features with the same sites." />
              </div>
            )}
            <div style={{ fontWeight: 'bolder', marginTop: '10px' }}><FormattedMessage id="CMS" /></div>
            <div className="dropdown">
              <button
                style={{ width: '100%', textAlign: 'left', backgroundColor: 'white', color: 'black', border: '1px solid #ced4da' }}
                className="btn btn-secondary form-select"
                type="button"
                id="cmsDropdownMenu"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {cms ? cmses.find(c => c.id === cms)?.name : <FormattedMessage id="SELECT_CMS" />}
              </button>
              <ul
                style={{ textAlign: 'left'}}
                className="dropdown-menu"
                aria-labelledby="cmsDropdownMenu"
              >
                {cmses.map(cmsObj => (
                  <li id="cmsItem" key={cmsObj.id} onClick={() => handleCmsClick(cmsObj.id)}>
                    <span className="dropdown-item">{cmsObj.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            {cmsDropdownError && <div className="text-danger" role="alert">{cmsDropdownError}</div>}
            <div style={{ fontWeight: 'bolder', marginTop: '10px' }}><FormattedMessage id="SITE" /></div>
            <div className="dropdown">
              <button
                style={{ width: '100%', textAlign: 'left', backgroundColor: 'white', color: 'black', border: '1px solid #ced4da', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                className="btn btn-secondary form-select"
                type="button"
                id="siteDropdownMenu"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={!hasSites}
                data-bs-display="static"
              >
                <span style={{ fontWeight: 'bold' }}>{selectedSitesCount} <FormattedMessage id="SELECTED_TEXT" /></span>
                <span style={{ color: '#6c757d', marginLeft: '10px' }}>{selectedSitesText}</span>
              </button>
              <ul
                style={{ textAlign: 'left' }}
                className="dropdown-menu dropdown-menu-start"
                aria-labelledby="siteDropdownMenu"
              >
                {siteComponents}
              </ul>
            </div>
            {siteDropdownError && <div className="text-danger" role="alert">{siteDropdownError}</div>}
          </div>
          <div className="modal-footer justify-content-between">
            <button id="assignSiteBackBtn" className="btn back-btn" type="button" data-bs-dismiss="modal"><FormattedMessage id="BACK" /></button>
            <button id="assignSiteSaveBtn" className="btn save-btn ms-2" type="submit" onClick={onSave}><FormattedMessage id="SAVE" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsersAssignSiteModal;
